/**
 * @file Assemblage du HUD selon le preset du mode (combat / minimal / hub) : TopBar, StageBar, BuffBar, ControlToggles, SideMenu, UpgradePanel, FloatingTexts, JoystickView.
 */
import type { HudPreset } from '@config/types';
import { Component } from '../core/Component';
import { h } from '../core/dom';
import type { UiContext } from '../UiContext';
import { BuffBar } from './BuffBar';
import { CatnipCounter } from './CatnipCounter';
import { ControlToggles } from './ControlToggles';
import { FloatingTexts } from './FloatingTexts';
import { HubPresence } from './HubPresence';
import { HubSocial } from './HubSocial';
import { HubBar } from './HubBar';
import { JoystickView } from './JoystickView';
import { SideMenu } from './SideMenu';
import { StageBar } from './StageBar';
import { TopBar } from './TopBar';
import { UpgradePanel } from './UpgradePanel';
import { ZoomControls } from './ZoomControls';

const PRESETS: readonly HudPreset[] = ['combat', 'minimal', 'hub'];

export class Hud extends Component {
  private topBar!: TopBar;
  private stageBar!: StageBar;
  private buffBar!: BuffBar;
  private controls!: ControlToggles;
  private zoom!: ZoomControls;
  private catnip!: CatnipCounter;
  private hubBar!: HubBar;
  private presence: HubPresence | null = null;
  private social: HubSocial | null = null;
  private sideMenu!: SideMenu;
  private sideTop!: SideMenu;
  private sideBottom!: SideMenu;
  private upgrades!: UpgradePanel;
  private floaters!: FloatingTexts;
  private joystick!: JoystickView;
  private preset: HudPreset | null = null;

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'hud');
  }

  protected build(): void {
    // Couches libres d'abord (derriere les controles) : nombres flottants et joystick.
    this.floaters = new FloatingTexts(this.ctx).mount(this.el);
    this.joystick = new JoystickView(this.ctx).mount(this.el);

    // Disposition de l'original : bandeau de progression + piste de niveaux en haut, soldes a droite ; colonne gauche : Deplacement auto /
    // Tir auto en haut, buffs + Renaissance / Skins / Armes en bas ; colonne droite : Missions / Cadeau en haut, Hub / Modes en bas ;
    // 3 cartes d'amelioration.
    const top = h('div', { class: 'hud-top' });
    const leftTop = h('div', { class: 'hud-left-top' });
    const leftBottom = h('div', { class: 'hud-left-bottom' });
    const left = h('div', { class: 'hud-left' }, leftTop, leftBottom);
    const rightTop = h('div', { class: 'hud-right-top' });
    const rightBottom = h('div', { class: 'hud-right-bottom' });
    const right = h('div', { class: 'hud-right' }, rightTop, rightBottom);
    const bottom = h('div', { class: 'hud-bottom' });
    this.el.append(top, left, right, bottom);

    this.topBar = new TopBar(this.ctx).mount(top);
    this.stageBar = new StageBar(this.ctx).mount(top);
    this.controls = new ControlToggles(this.ctx).mount(leftTop);
    this.zoom = new ZoomControls(this.ctx).mount(leftTop);
    this.catnip = new CatnipCounter(this.ctx).mount(leftTop);
    if (this.ctx.hub) {
      this.presence = new HubPresence(this.ctx).mount(leftTop);
      this.addDisposer(() => this.presence?.destroy());
    }
    this.buffBar = new BuffBar(this.ctx).mount(leftBottom);
    this.sideMenu = new SideMenu(this.ctx, ['rebirth', 'skins', 'weapons']).mount(leftBottom);
    this.sideTop = new SideMenu(this.ctx, ['missions', 'daily', 'online']).mount(rightTop);
    this.sideBottom = new SideMenu(this.ctx, ['hub', 'modes']).mount(rightBottom);
    this.upgrades = new UpgradePanel(this.ctx).mount(bottom);
    if (this.ctx.hub || this.ctx.online) {
      this.social = new HubSocial(this.ctx).mount(bottom);
      this.addDisposer(() => this.social?.destroy());
    }
    this.hubBar = new HubBar(this.ctx).mount(bottom);

    for (const c of [this.floaters, this.joystick, this.topBar, this.stageBar, this.controls, this.buffBar, this.sideMenu, this.sideTop, this.sideBottom, this.upgrades, this.zoom, this.catnip, this.hubBar]) {
      this.addDisposer(() => c.destroy());
    }
    this.applyPreset(this.ctx.sim.getView().rules.hud);
  }

  private applyPreset(p: HudPreset): void {
    if (p === this.preset) return;
    this.preset = p;
    for (const name of PRESETS) this.el.classList.toggle(`preset-${name}`, name === p);
  }

  override update(dt: number): void {
    const preset = this.ctx.sim.getView().rules.hud;
    this.applyPreset(preset);
    this.floaters.update(dt);
    this.topBar.update(dt);
    this.stageBar.update(dt);
    if (preset === 'combat') {
      this.buffBar.update(dt);
      this.sideMenu.update(dt);
      this.sideTop.update(dt);
      this.sideBottom.update(dt);
      this.upgrades.update(dt);
    }
    if (preset === 'hub') {
      this.hubBar.update(dt);
      this.catnip.update(dt);
      this.presence?.update(dt);
      this.social?.update(dt);
    }
    else if (preset !== 'minimal') this.controls.update(dt);
  }
}
